import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import FolderTree from '../FolderTree';

const breakpoints = {
  mobile: '768px',
  tablet: '1024px',
};

const SidebarRoot = styled.aside`
  width: 260px;
  background: #ffffff;
  border-right: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;

  @media (max-width: ${breakpoints.tablet}) {
    width: 240px;
  }

  @media (max-width: ${breakpoints.mobile}) {
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    z-index: 90;
    transform: translateX(${({ isOpen }) => (isOpen ? '0' : '-100%')});
    transition: transform 0.25s ease;
    box-shadow: 2px 0 12px rgba(15, 23, 42, 0.08);
  }
`;

const Backdrop = styled.div`
  display: none;

  @media (max-width: ${breakpoints.mobile}) {
    display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: 89;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.95rem 1rem 0.65rem;
  border-bottom: 1px solid #f3f4f6;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #111827;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.15rem;
`;

const IconBtn = styled.button`
  width: 28px;
  height: 28px;
  border: none;
  background: none;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #6b7280;
  transition: all 0.12s ease;

  &:hover {
    background: #f3f4f6;
    color: #2563eb;
  }
`;

const TreeScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem 0.5rem 0.75rem;
`;

const StorageWidget = styled.div`
  margin: 0.6rem;
  padding: 0.85rem;
  background: #f9fafb;
  border: 1px solid #f3f4f6;
  border-radius: 10px;
`;

const StorageTopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.45rem;
`;

const StorageLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: #4b5563;
`;

const StorageGear = styled.button`
  width: 22px;
  height: 22px;
  border: none;
  background: none;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #9ca3af;

  &:hover {
    background: #ffffff;
    color: #2563eb;
  }
`;

const StorageValueRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 0.45rem;
`;

const StorageValue = styled.span`
  font-size: 0.85rem;
  font-weight: 600;
  color: #111827;
`;

const StoragePct = styled.span`
  font-size: 0.78rem;
  font-weight: 600;
  color: #2563eb;
`;

const Track = styled.div`
  height: 6px;
  background: #e5e7eb;
  border-radius: 3px;
  overflow: hidden;
`;

const Fill = styled.div`
  height: 100%;
  width: ${({ pct }) => Math.min(100, pct)}%;
  background: linear-gradient(90deg, #3b82f6, #2563eb);
  transition: width 0.3s ease;
`;

const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const RefreshIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

const CollapseIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="11 17 6 12 11 7" />
    <polyline points="18 17 13 12 18 7" />
  </svg>
);

const GearIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

const formatBytes = (bytes) => {
  if (bytes == null || isNaN(bytes)) return '-';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(2)} ${units[i]}`;
};

const Sidebar = ({
  isOpen = true,
  onClose,
  selectedRemote,
  currentPath,
  setCurrentPath,
  capacityBytes = 100 * 1024 * 1024 * 1024, // 기본 100GB 가정 (실제 quota 없음)
}) => {
  const [refreshKey, setRefreshKey] = useState(0);
  const [stats, setStats] = useState(null);

  const handleStats = useCallback((s) => setStats(s), []);

  const usedBytes = stats?.totalSize ?? 0;
  const pct = Math.min(100, Math.round((usedBytes / capacityBytes) * 1000) / 10);

  return (
    <>
      <Backdrop isOpen={isOpen} onClick={onClose} />
      <SidebarRoot isOpen={isOpen}>
        <Header>
          <Title>폴더</Title>
          <HeaderActions>
            <IconBtn aria-label="새 폴더" disabled>
              <PlusIcon />
            </IconBtn>
            <IconBtn
              aria-label="새로고침"
              onClick={() => setRefreshKey((k) => k + 1)}
            >
              <RefreshIcon />
            </IconBtn>
            <IconBtn aria-label="접기" onClick={onClose}>
              <CollapseIcon />
            </IconBtn>
          </HeaderActions>
        </Header>

        <TreeScroll>
          <FolderTree
            selectedRemote={selectedRemote}
            currentPath={currentPath || ''}
            setCurrentPath={(p) => {
              if (setCurrentPath) setCurrentPath(p);
              if (onClose && window.innerWidth <= 768) onClose();
            }}
            refreshKey={refreshKey}
            onStats={handleStats}
          />
        </TreeScroll>

        <StorageWidget>
          <StorageTopRow>
            <StorageLabel>저장소 사용량</StorageLabel>
            <StorageGear aria-label="설정"><GearIcon /></StorageGear>
          </StorageTopRow>
          <StorageValueRow>
            <StorageValue>{formatBytes(usedBytes)} / {formatBytes(capacityBytes)}</StorageValue>
            <StoragePct>{pct}%</StoragePct>
          </StorageValueRow>
          <Track><Fill pct={pct} /></Track>
        </StorageWidget>
      </SidebarRoot>
    </>
  );
};

export default Sidebar;
